import { ActionName } from 'api/endpoints/action-names';
import { ContractActionName } from 'api/endpoints/contractActionNames';
import {
  OnAddSave,
  OnEditSave,
} from 'components/UIComponents/EntityTable/EntityTable.types';
import { useAppDispatch, useAppSelector } from 'hooks/reducerHooks';
import { SectionType } from 'pages/ClaimsContractsToolPage/types';
import { useCallback, useEffect } from 'react';
import {
  createContractActionNameThunk,
  editAContractsctionNameThunk,
  fetchContractActionNamesThunk,
  fetchContractActionsSelectOptions,
} from 'reducer/thunks/actions-thunks';
import { FetchStatus } from 'types/fetch-status.types';

export type ActionNameData = {
  data: ActionName[];
  status: FetchStatus;
};

export function useEditAndAddActionNames(section?: SectionType) {
  const dispatch = useAppDispatch();
  const { status, contractsActionNames } = useActionNames({ section });

  const onContractEditSave: OnEditSave<ContractActionName> = useCallback(
    async (formValues, item: ContractActionName) => {
      const result = await dispatch(
        editAContractsctionNameThunk({
          id: item.id,
          contractExhibitTypeId: Number(formValues.contractExhibitTypeId),
          contractActionName: formValues.contractActionName as string,
          hearingDate: formValues.hearingDate as string,
        }),
      );

      return result;
    },
    [dispatch],
  );

  const onContractAddSave: OnAddSave<ContractActionName> = useCallback(
    async (formValues) => {
      const result = await dispatch(
        createContractActionNameThunk({
          contractActionName: formValues.contractActionName as string,
          hearingDate: formValues.hearingDate as string,
          contractExhibitTypeId: Number(formValues.contractExhibitTypeId),
        }),
      );

      return result;
    },
    [dispatch],
  );

  return {
    status,
    onContractEditSave,
    onContractAddSave,
    contractsActionNames,
  };
}

export function useActionNames({ section }: { section?: SectionType }) {
  const actionNamesSlice = useAppSelector((s) => s.actionNames);
  const { contractsData, contractsStatus } = actionNamesSlice;
  const dispatch = useAppDispatch();

  const isIdle =
    section === SectionType.Contracts && contractsStatus === FetchStatus.Idle;

  useEffect(() => {
    if (isIdle) {
      dispatch(fetchContractActionNamesThunk());
    }
  }, [dispatch, isIdle]);

  return {
    status: contractsStatus,
    contractsActionNames: contractsData,
  };
}

export function useActionsSelectOptions(section: SectionType) {
  const actionsSelectOptions = useAppSelector((s) => s.actionsSelectOptions);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (actionsSelectOptions.type === FetchStatus.Idle) {
      if (section === SectionType.Contracts)
        dispatch(fetchContractActionsSelectOptions());
    }
  }, [actionsSelectOptions.type, dispatch, section]);

  return {
    actionNameOptions: actionsSelectOptions,
    status: actionsSelectOptions.type,
  };
}
